import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (!email.trim()) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('✅ Password reset link sent! Check your email inbox.');
      setSubmitted(true);
      setEmail('');
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setError('❌ No account found with this email address');
      } else if (err.code === 'auth/invalid-email') {
        setError('❌ Please enter a valid email address');
      } else {
        setError('❌ Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.pageContainer}>
      <div style={styles.background} />
      
      <div style={styles.mainContent}>
        <div style={styles.container}>
          {/* Left Section - Brand */}
          <div style={styles.brandSection}>
            <div style={styles.brandContent}>
              <h1 style={styles.brandTitle}>Password Recovery</h1>
              <p style={styles.brandSubtitle}>
                Don't worry! It happens to the best of us. We'll help you reset your password and get back to learning.
              </p>
              <div style={styles.features}>
                <div style={styles.feature}>
                  <span style={styles.featureIcon}>🔐</span>
                  <span>Secure process</span>
                </div>
                <div style={styles.feature}>
                  <span style={styles.featureIcon}>📧</span>
                  <span>Email verification</span>
                </div>
                <div style={styles.feature}>
                  <span style={styles.featureIcon}>⚡</span>
                  <span>Instant reset link</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Form */}
          <div style={styles.formSection}>
            <div style={styles.formCard}>
              <h2 style={styles.formTitle}>Reset Password</h2>
              <p style={styles.formSubtitle}>Enter your email to receive a password reset link</p>

              {error && (
                <div style={styles.errorBanner}>
                  <span>{error}</span>
                </div>
              )}

              {message && (
                <div style={styles.successBanner}>
                  <span>{message}</span>
                </div>
              )}

              {!submitted ? (
                <form onSubmit={handleResetPassword} style={styles.form}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      style={styles.input}
                      disabled={loading}
                      autoComplete="email"
                    />
                  </div>

                  <button
                    type="submit"
                    style={{
                      ...styles.submitButton,
                      opacity: loading ? 0.7 : 1,
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
              ) : (
                <div style={styles.successContent}>
                  <div style={styles.successIcon}>📬</div>
                  <p style={styles.successMessage}>
                    We've sent a password reset link to <strong>{email}</strong>
                  </p>
                  <p style={styles.successSubtext}>
                    Check your email and follow the link to create a new password. The link expires in 1 hour.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setEmail('');
                    }}
                    style={styles.resetButton}
                  >
                    Send Another Email
                  </button>
                </div>
              )}

              <div style={styles.divider} />

              <div style={styles.backToLogin}>
                <p style={styles.backText}>
                  Remember your password?{' '}
                  <span
                    onClick={() => navigate('/login')}
                    style={styles.backLink}
                  >
                    Back to Login
                  </span>
                </p>
              </div>

              {/* Security Footer */}
              <div style={styles.footer}>
                <p style={styles.footerText}>
                  🔒 Your security is our priority
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============= STYLES =============
const styles = {
  pageContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#f8f9fa'
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    zIndex: -1
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '40px',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 20px',
    width: '100%'
  },
  brandSection: {
    color: '#fff',
    padding: '40px 20px',
    display: 'flex',
    alignItems: 'center'
  },
  brandContent: {
    maxWidth: '500px'
  },
  brandTitle: {
    fontSize: '48px',
    fontWeight: '700',
    marginBottom: '16px',
    lineHeight: '1.2'
  },
  brandSubtitle: {
    fontSize: '20px',
    marginBottom: '40px',
    opacity: 0.95,
    lineHeight: '1.5'
  },
  features: {
    display: 'grid',
    gap: '20px',
    marginTop: '40px'
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    fontSize: '16px'
  },
  featureIcon: {
    fontSize: '32px'
  },
  formSection: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  formCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '40px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    width: '100%',
    maxWidth: '420px'
  },
  formTitle: {
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '8px',
    color: '#333'
  },
  formSubtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '24px'
  },
  errorBanner: {
    backgroundColor: '#fff5f5',
    border: '1px solid #fc8181',
    color: '#c53030',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: '500'
  },
  successBanner: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #86efac',
    color: '#16a34a',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: '500'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333'
  },
  input: {
    padding: '14px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '15px',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
    backgroundColor: '#fafafa',
    width: '100%',
    boxSizing: 'border-box'
  },
  submitButton: {
    padding: '14px 20px',
    backgroundColor: '#27b05b',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'background-color 0.3s'
  },
  successContent: {
    textAlign: 'center',
    padding: '20px 0'
  },
  successIcon: {
    fontSize: '64px',
    marginBottom: '20px'
  },
  successMessage: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px'
  },
  successSubtext: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '20px',
    lineHeight: '1.5'
  },
  resetButton: {
    padding: '12px 20px',
    backgroundColor: '#27b05b',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    marginTop: '12px',
    width: '100%'
  },
  divider: {
    height: '1px',
    backgroundColor: '#e0e0e0',
    margin: '24px 0'
  },
  backToLogin: {
    textAlign: 'center'
  },
  backText: {
    fontSize: '14px',
    color: '#666'
  },
  backLink: {
    color: '#27b05b',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  footer: {
    marginTop: '20px',
    textAlign: 'center'
  },
  footerText: {
    fontSize: '12px',
    color: '#999'
  }
};

export default ForgotPassword;
